import CardMentionEditor from './CardMentionEditor';
import { Focusable } from './ui/Focusable';
import { UI } from '../utils/arena/uiSounds';
import { TEXT_BODY, RADIUS, innerRadius } from '../lib/medievalTheme';
import { t } from '../localization/t';

// The DM composer, as one control.
//
// Same shape as CommentComposer (deck notes / replay threads): a single
// rounded frame IS the input, and the buttons live inside it. The chat
// composer used to be three free-floating shapes on the panel, a circle, a
// pill and a circle, which put three competing radii and three separate
// grounds on one row and left the send button reading as something that
// happened to sit next to the field rather than part of it.
//
// The one thing this adds over CommentComposer is the attach control, which
// takes the frame's other inside corner. Both buttons are square and share
// the frame's inner corner (`md` against the frame's `xl`), so the three
// curves read as one family.
//
// `children` are the attach menu, the moment / replay pickers and their
// dismiss backdrop. They anchor off this component's wrapper, which is the
// positioned ancestor they measure from.

// The gap around the frame, the same on all four sides. It has to be one
// number: the frame's corner is the panel's corner minus this gap, and a gap
// that differed per axis would leave no single radius that could be
// concentric with the panel on both.
const PANEL_INSET = '0.5cqw';
// Uniform on every side, so the buttons sit exactly as far from the bottom
// edge as from their own side edge.
const FRAME_PAD = '0.55cqw';
const BTN_SIZE = '1.85cqw';
// The editor's empty height matches the buttons, so a single line of text
// sits level with them. padding-top + line-height + padding-bottom must
// equal BTN_SIZE exactly, or the caret drifts off their centre line at
// canvas sizes where the rounding differs.
const EDITOR_LINE = '1.25cqw';
const EDITOR_PAD_Y = '0.3cqw';

function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ width: '0.95cqw', height: '0.95cqw' }}
    >
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round"
      style={{ width: '0.95cqw', height: '0.95cqw' }}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export default function ChatComposer({
  value,
  onValueChange,
  onSubmit,
  canSend,
  attachOpen = false,
  onToggleAttach,
  placeholder = t('Type a message...'),
  maxLength = 500,
  sorceryCards,
  onOpenCard,
  onCardHover,
  onCardHoverEnd,
  inset = PANEL_INSET,
  panelRadius = RADIUS['2xl'],
  children = null,
}) {
  return (
    <div className="shrink-0 relative" style={{ padding: inset }}>
      {children}
      <div
        className="vh-chat-composer flex items-end"
        style={{
          padding: FRAME_PAD,
          gap: FRAME_PAD,
          // Concentric with the panel: its corner less the gap this frame
          // sits inside it.
          borderRadius: innerRadius(panelRadius, inset),
        }}
      >
        <button
          type="button"
          className={`vh-chat-composer-btn is-ghost relative z-20 ${attachOpen ? 'is-open' : ''}`}
          title={t('Attach cards or gold')}
          aria-label={t('Attach cards or gold')}
          aria-expanded={attachOpen}
          onClick={onToggleAttach}
        >
          <PlusIcon />
        </button>

        <CardMentionEditor
          className="flex-1 text-xs"
          style={{
            color: TEXT_BODY,
            lineHeight: EDITOR_LINE,
            padding: `${EDITOR_PAD_Y} 0.25cqw`,
            minHeight: BTN_SIZE,
            // Past this the editor scrolls internally rather than growing
            // the composer and pushing the message list around.
            maxHeight: '5cqw',
            position: 'relative',
          }}
          placeholder={placeholder}
          maxLength={maxLength}
          sorceryCards={sorceryCards}
          value={value}
          onValueChange={onValueChange}
          onSubmit={onSubmit}
          onOpenCard={onOpenCard}
          onCardHover={onCardHover}
          onCardHoverEnd={onCardHoverEnd}
        />

        <Focusable id="chat-send-btn" asChild onActivate={onSubmit}>
          <button
            type="button"
            className={`vh-chat-composer-btn is-send ${canSend ? 'is-ready' : ''}`}
            data-sound={UI.CONFIRM}
            disabled={!canSend}
            title={t('Send message (Enter)')}
            aria-label={t('Send message')}
            onClick={onSubmit}
          >
            <SendIcon />
          </button>
        </Focusable>
      </div>
    </div>
  );
}
